import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { XIcon, Loader2Icon, CalendarIcon } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
// Validation Schema
const createAcademicYearSchema = z.object({
  name: z.string().min(1, 'Academic year name is required'),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required')
}).refine(data => {
  if (data.startDate && data.endDate) {
    return new Date(data.startDate) < new Date(data.endDate);
  }
  return true;
}, {
  message: 'End date must be after start date',
  path: ['endDate']
});
type CreateAcademicYearFormValues = z.infer<typeof createAcademicYearSchema>;
interface CreateAcademicYearModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: CreateAcademicYearFormValues) => Promise<void>;
  hasActiveYear: boolean;
}
export const CreateAcademicYearModal: React.FC<CreateAcademicYearModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  hasActiveYear
}) => {
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isSubmitting
    },
    reset,
    watch
  } = useForm<CreateAcademicYearFormValues>({
    resolver: zodResolver(createAcademicYearSchema)
  });
  const onSubmit = async (data: CreateAcademicYearFormValues) => {
    try {
      await onCreate(data);
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to create academic year:', error);
    }
  };
  const startDate = watch('startDate');
  return <Dialog.Root open={isOpen} onOpenChange={onClose} data-id="element-999">
      <AnimatePresence data-id="element-1000">
        {isOpen && <Dialog.Portal forceMount container={document.body} data-id="element-1001">
            <Dialog.Overlay asChild data-id="element-1002">
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{
            zIndex: 9998
          }} data-id="element-1003">
                <Dialog.Content asChild data-id="element-1004">
                  <motion.div initial={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} className="w-full max-w-md max-h-[85vh] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden relative" style={{
                zIndex: 9999
              }} onClick={e => e.stopPropagation()} data-id="element-1005">
                    {/* Header - Fixed */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50 flex-shrink-0" data-id="element-1006">
                      <div className="flex items-center gap-3" data-id="element-1007">
                        <div className="w-10 h-10 rounded-xl bg-[#2563EB]/10 flex items-center justify-center text-[#2563EB]" data-id="element-1008">
                          <CalendarIcon size={20} data-id="element-1009" />
                        </div>
                        <div data-id="element-1010">
                          <Dialog.Title className="text-lg font-bold text-text-primary" data-id="element-1011">
                            Create Academic Year
                          </Dialog.Title>
                          <Dialog.Description className="text-xs text-text-secondary" data-id="element-1012">
                            Add a new school year to your organization
                          </Dialog.Description>
                        </div>
                      </div>
                      <button onClick={onClose} className="p-2 text-text-tertiary hover:text-text-primary hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1013">
                        <XIcon size={20} data-id="element-1014" />
                      </button>
                    </div>

                    {/* Form - Scrollable */}
                    <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto" data-id="element-1015">
                      <div className="p-6 space-y-5" data-id="element-1016">
                        {/* Name Field */}
                        <div className="space-y-1.5" data-id="element-1017">
                          <label className="text-sm font-semibold text-text-primary" data-id="element-1018">
                            Academic Year Name{' '}
                            <span className="text-red-500" data-id="element-1019">*</span>
                          </label>
                          <input {...register('name')} placeholder="e.g. 2025-2026" className={`w-full px-3 py-2.5 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 transition-all ${errors.name ? 'border-red-300 focus:border-red-500' : 'border-custom focus:border-[#2563EB]'}`} data-id="element-1020" />
                          {errors.name && <p className="text-xs text-red-500 font-medium" data-id="element-1021">
                              {errors.name.message}
                            </p>}
                        </div>

                        <div className="grid grid-cols-2 gap-4" data-id="element-1022">
                          {/* Start Date Field */}
                          <div className="space-y-1.5" data-id="element-1023">
                            <label className="text-sm font-semibold text-text-primary" data-id="element-1024">
                              Start Date <span className="text-red-500" data-id="element-1025">*</span>
                            </label>
                            <input type="date" {...register('startDate')} className={`w-full px-3 py-2.5 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 transition-all ${errors.startDate ? 'border-red-300 focus:border-red-500' : 'border-custom focus:border-[#2563EB]'}`} data-id="element-1026" />
                            {errors.startDate && <p className="text-xs text-red-500 font-medium" data-id="element-1027">
                                {errors.startDate.message}
                              </p>}
                          </div>

                          {/* End Date Field */}
                          <div className="space-y-1.5" data-id="element-1028">
                            <label className="text-sm font-semibold text-text-primary" data-id="element-1029">
                              End Date <span className="text-red-500" data-id="element-1030">*</span>
                            </label>
                            <input type="date" {...register('endDate')} min={startDate} className={`w-full px-3 py-2.5 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 transition-all ${errors.endDate ? 'border-red-300 focus:border-red-500' : 'border-custom focus:border-[#2563EB]'}`} data-id="element-1031" />
                            {errors.endDate && <p className="text-xs text-red-500 font-medium" data-id="element-1032">
                                {errors.endDate.message}
                              </p>}
                          </div>
                        </div>

                        {/* Info Box */}
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-1033">
                          <p className="text-xs text-text-secondary leading-relaxed" data-id="element-1034">
                            <span className="font-semibold text-text-primary" data-id="element-1035">
                              Note:
                            </span>{' '}
                            You can set this year as active from the list after
                            creating it.
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="pt-4 flex items-center gap-3" data-id="element-1036">
                          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-custom rounded-xl text-sm font-semibold text-text-secondary hover:bg-gray-50 transition-colors" data-id="element-1037">
                            Cancel
                          </button>
                          <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2.5 bg-[#2563EB] text-white rounded-xl text-sm font-semibold hover:bg-[#1d4ed8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1038">
                            {isSubmitting ? <>
                                <Loader2Icon size={16} className="animate-spin" data-id="element-1039" />
                                Creating...
                              </> : 'Create Year'}
                          </button>
                        </div>
                      </div>
                    </form>
                  </motion.div>
                </Dialog.Content>
              </motion.div>
            </Dialog.Overlay>
          </Dialog.Portal>}
      </AnimatePresence>
    </Dialog.Root>;
};